export const CUSTOMER_ADDRESS_TYPE_OPTIONS = [
  { value: 'BILLING', label: '帳單' },
  { value: 'SHIPPING', label: '出貨' },
  { value: 'BOTH', label: '帳單＋出貨' },
];

export function normalizeAddressType(value) {
  if (value == null || value === '') {
    return '';
  }
  return String(value).trim().toUpperCase();
}

export function addressTypeCovers(type, purpose) {
  const normalized = normalizeAddressType(type);
  if (normalized === 'BOTH') {
    return true;
  }
  return normalized === purpose;
}

export function addressTypesOverlap(leftType, rightType) {
  const left = normalizeAddressType(leftType);
  const right = normalizeAddressType(rightType);
  if (!left || !right) {
    return false;
  }
  if (left === 'BOTH' || right === 'BOTH') {
    return true;
  }
  return left === right;
}

export function formatCustomerAddressTypeLabel(type) {
  const normalized = normalizeAddressType(type);
  const match = CUSTOMER_ADDRESS_TYPE_OPTIONS.find((option) => option.value === normalized);
  return match?.label ?? (normalized || '—');
}

export function formatCustomerAddressSelectLabel(row) {
  if (!row) {
    return '—';
  }
  const typeLabel = formatCustomerAddressTypeLabel(row.addressType);
  const location = [row.postalCode, row.address].filter(Boolean).join(' ');
  const text = [typeLabel, location].filter(Boolean).join(' · ') || String(row.id);
  return row.isDefault ? `${text}（預設）` : text;
}

/**
 * Customer detail `addresses[]` → FloatingSelect options。
 * value＝`address.id`（detail 必須有 id）。
 * @param {Array} addresses
 * @param {'BILLING'|'SHIPPING'} purpose
 */
export function mapCustomerAddressSelectOptions(addresses = [], purpose) {
  return (addresses ?? [])
    .filter((row) => row?.id && addressTypeCovers(row.addressType, purpose))
    .map((row) => ({
      value: row.id,
      label: formatCustomerAddressSelectLabel(row),
    }));
}

export function pickDefaultCustomerAddressId(addresses = [], purpose) {
  const matched = (addresses ?? []).filter(
    (row) => row?.id && addressTypeCovers(row.addressType, purpose),
  );
  const preferred = matched.find((row) => row.isDefault) ?? matched[0];
  return preferred?.id ?? '';
}

export function pickDefaultCustomerAddress(addresses = [], purpose) {
  const matched = (addresses ?? []).filter(
    (row) => addressTypeCovers(row?.addressType, purpose),
  );
  return matched.find((row) => row.isDefault) ?? matched[0] ?? null;
}

/**
 * 預設聯絡人：isDefault 優先，否則第一筆。
 * @param {Array} contacts
 */
export function pickDefaultCustomerContact(contacts = []) {
  const list = contacts ?? [];
  if (list.length === 0) {
    return null;
  }
  return list.find((row) => row?.isDefault) ?? list[0];
}

export function pickDefaultCustomerContactId(contacts = []) {
  return pickDefaultCustomerContact(contacts)?.id ?? '';
}

export function formatCustomerContactSelectLabel(contact) {
  if (!contact) {
    return '—';
  }
  const title = [contact.department, contact.title].filter(Boolean).join(' ');
  const phone = contact.mobile || contact.phone || '';
  const text = [contact.name, title, phone].filter(Boolean).join(' · ')
    || String(contact.id);
  return contact.isDefault ? `${text}（預設）` : text;
}

/**
 * Customer detail `contacts[]` → FloatingSelect options。
 * value＝`contact.id`。
 */
export function mapCustomerContactSelectOptions(contacts = []) {
  return (contacts ?? [])
    .filter((row) => row?.id)
    .map((row) => ({
      value: row.id,
      label: formatCustomerContactSelectLabel(row),
    }));
}

export function ensureSelectOption(options = [], value, label) {
  if (!value) {
    return options;
  }
  if (options.some((option) => option.value === value)) {
    return options;
  }
  return [...options, { value, label: label || String(value) }];
}
